/* ═══════════════════════════════════════════════════════════════════════════
   Telemetry, where it meets React
   ───────────────────────────────────────────────────────────────────────────
   Copied verbatim between the three repos alongside `telemetry.ts`. Two
   things live here rather than there because they need React, and `telemetry.ts`
   is deliberately importable from anywhere — including from the HTTP client,
   which must not drag a renderer in behind it.

   The boundary is **headless**: it catches, reports, and hands the error to a
   `fallback` the app supplies. Three apps with one design system each and no
   shared component library means a shipped fallback UI here would be wrong in
   at least two of them, and the screen a user sees only when something has
   already broken is the last place to accept "close enough".
   ═══════════════════════════════════════════════════════════════════════════ */

import { Component, useEffect, useRef, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { addBreadcrumb, captureError, trackScreen } from './telemetry'

/* ── route tracking ────────────────────────────────────────────────────────── */

/** Records a screen view on every route change. Renders nothing.
 *
 *  Mount it inside the router and above the route table. Two reasons it is a
 *  component rather than a call in each screen: a screen added later would be
 *  missed, and a redirect that never renders anything still needs to appear in
 *  the trail — otherwise a breadcrumb list reads as a jump from one screen to
 *  a distant one with no explanation. */
export function TelemetryRoutes() {
  const { pathname } = useLocation()
  /* React 19 in StrictMode mounts effects twice in development. Without this,
     every navigation is recorded twice and the breadcrumb trail is half
     duplicates — cosmetic in Analytics, actively misleading in a report. */
  const last = useRef<string | null>(null)

  useEffect(() => {
    if (last.current === pathname) return
    last.current = pathname
    trackScreen(pathname)
  }, [pathname])

  return null
}

/* ── the boundary ──────────────────────────────────────────────────────────── */

interface BoundaryProps {
  children: ReactNode
  /** What to render instead of the subtree. Given the error and a `retry` that
   *  clears the boundary — worth offering where the failure might be transient,
   *  and worth *not* offering after a chunk load failure, where only a reload
   *  can help. */
  fallback: (error: unknown, retry: () => void) => ReactNode
  /** Distinguishes one boundary from another in the report. `screen` for the
   *  route-level one, a component name for a narrower one. */
  scope?: string
  /** Ran in addition to the report — the app's own logging, a toast, a
   *  recovery attempt. Errors thrown here are swallowed. */
  onError?: (error: unknown, info: ErrorInfo) => void
}

interface BoundaryState {
  error: unknown
}

/** Catches a render error, reports it with its component stack, and renders the
 *  app's fallback.
 *
 *  The component stack is the reason this exists at all rather than relying on
 *  the global `error` handler: React catches render errors itself, so they
 *  never reach `window.onerror`, and without a boundary they are invisible to
 *  telemetry *and* they unmount the whole tree. Of every failure this system
 *  reports, this is the one the user notices most and reports least — the app
 *  simply goes blank, and a blank page generates no bug report. */
export class TelemetryBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null }

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    captureError(error, {
      kind: 'react',
      ...(info.componentStack ? { componentStack: info.componentStack } : {}),
      ...(this.props.scope ? { context: { scope: this.props.scope } } : {}),
    })
    try {
      this.props.onError?.(error, info)
    } catch {
      /* A failing handler must not replace one broken screen with two. */
    }
  }

  private readonly retry = (): void => {
    addBreadcrumb('action', 'error boundary retry')
    this.setState({ error: null })
  }

  override render(): ReactNode {
    if (this.state.error !== null) return this.props.fallback(this.state.error, this.retry)
    return this.props.children
  }
}
